import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks/storeHooks";
import { fetchAnswers, fetchInspectionsList } from "../store/thunks/answerThunks";
import { selectAnswer, selectSelectedInspection } from "../store/selectors/answerSelectors";
import { setSelectedInspection } from "../store/slices/answerSlice";
import { ProgressSpinner } from "primereact/progressspinner";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { AnswerTable } from "../components/Answer/AnswerTable";
import { Inspection } from "../types/inspection";

const AnswersView = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { inspections, loadingInspections, loading, error, data } = useAppSelector(selectAnswer);
    const selectedInspection = useAppSelector(selectSelectedInspection);

    // Load inspections list if not already loaded
    useEffect(() => {
        if (inspections.length === 0 && !loadingInspections) {
            dispatch(fetchInspectionsList());
        }
    }, [dispatch, inspections.length, loadingInspections]);

    // Find and set the selected inspection when inspections are loaded
    useEffect(() => {
        if (inspectionId && inspections.length > 0) {
            const inspection = inspections.find(
                (i: Inspection) => i.id === inspectionId || i.inspectionId === inspectionId
            );
            if (inspection && (!selectedInspection || selectedInspection.id !== inspection.id)) {
                dispatch(setSelectedInspection(inspection));
                dispatch(fetchAnswers(inspection.id));
            }
        }
    }, [dispatch, inspectionId, inspections, selectedInspection]);

    const handleBackClick = () => {
        navigate("/inspection/answers");
    };

    if (loadingInspections) {
        return (
            <div className="flex h-full align-items-center justify-content-center">
                <ProgressSpinner />
            </div>
        );
    }

    if (!selectedInspection) {
        return (
            <div className="flex flex-column align-items-center justify-content-center gap-3 p-5">
                <p>Inspection not found</p>
                <Button label="Back to Inspections" icon="pi pi-arrow-left" onClick={handleBackClick} />
            </div>
        );
    }

    const inspection = selectedInspection as Record<string, unknown>;
    const inspectionNumber = inspection.inspectionNumber as string | null;
    const inspectionIdValue = inspection.id as string;
    const vesselName = inspection.vesselName as string | null;
    const vesselId = inspection.vesselId as string | null;
    const location = inspection.location as string | null;
    const inspectionDate = inspection.inspectionDate as string | null;
    const checklistType = inspection.checklistType as string | null;
    const checklistVersion = inspection.checklistVersion as string | null;
    const status = inspection.status as string | null;

    return (
        <div className="flex flex-column gap-3">
            <Card>
                <div className="flex align-items-center gap-3 mb-3">
                    <Button
                        icon="pi pi-arrow-left"
                        severity="secondary"
                        text
                        onClick={handleBackClick}
                        tooltip="Back to Inspections"
                        tooltipOptions={{ position: "right" }}
                    />
                    <div className="flex-grow-1">
                        <div className="flex align-items-center gap-4 flex-wrap">
                            <div>
                                <span className="text-500 text-sm">Inspection</span>
                                <div className="font-bold text-xl">
                                    {inspectionNumber || inspectionIdValue}
                                </div>
                            </div>
                            {vesselName && (
                                <div>
                                    <span className="text-500 text-sm">Vessel</span>
                                    <div className="font-semibold">{vesselName}</div>
                                </div>
                            )}
                            {vesselId && (
                                <div>
                                    <span className="text-500 text-sm">IMO</span>
                                    <div className="font-semibold">{vesselId}</div>
                                </div>
                            )}
                            {location && (
                                <div>
                                    <span className="text-500 text-sm">Location</span>
                                    <div className="font-semibold">{location}</div>
                                </div>
                            )}
                            {inspectionDate && (
                                <div>
                                    <span className="text-500 text-sm">Date</span>
                                    <div className="font-semibold">
                                        {new Date(inspectionDate).toLocaleDateString()}
                                    </div>
                                </div>
                            )}
                            {checklistType && (
                                <div>
                                    <span className="text-500 text-sm">Checklist</span>
                                    <div className="font-semibold">
                                        {checklistType}
                                        {checklistVersion && ` v${checklistVersion}`}
                                    </div>
                                </div>
                            )}
                            {status && (
                                <div>
                                    <span className="text-500 text-sm">Status</span>
                                    <div className="font-semibold">{status}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Card>

            <Card>
                {loading ? (
                    <div className="flex align-items-center justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : error ? (
                    <div className="flex align-items-center justify-content-center p-5">
                        <p>Failed to load answers for this inspection :(</p>
                    </div>
                ) : data.length === 0 ? (
                    <div className="flex align-items-center justify-content-center p-5">
                        <p>No answers found for this inspection</p>
                    </div>
                ) : (
                    <AnswerTable />
                )}
            </Card>
        </div>
    );
};

export default AnswersView;
